import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";

import Handsontable from "handsontable";
import { registerAllModules } from "handsontable/registry";
import { ContextMenu } from "handsontable/plugins/contextMenu";

import { columns } from "../components/DeploymentsTable/config/columns";
import { subscriptionColumns } from "../components/DeploymentsTable/config/subscriptionColumns";
import { pendingColumns } from "../components/DeploymentsTable/config/pendingColumns";
import {
  getDeployments,
  updateDeployment,
  deleteDeployment,
  createDeployment,
} from "../components/DeploymentsTable/utils/deployments";

import {
  getTableState,
  saveTableState,
  resetTableState,
} from "../components/DeploymentsTable/utils/tableState";

// Connects to data-controller="deployments"
export default class extends Controller {
  static values = {
    url: String,
    visibleColumns: Array,
    columnSet: String,
    excludedStatuses: Array,
  };

  async connect() {
    const controller = this;
    registerAllModules();

    const deployments = await getDeployments(this.urlValue);
    this.rows = deployments.rows;

    this.currentUserId = document.head.querySelector(
      'meta[name="user_id"]',
    ).content;

    this.tableState = await getTableState();

    const columnSets = {
      subscriptions: subscriptionColumns,
      pending: pendingColumns,
    };

    const masterColumns = columnSets[this.columnSetValue] || columns;

    let visibleColumns = masterColumns;

    if (this.visibleColumnsValue.length > 0) {
      let cols = [];
      masterColumns.forEach((column) => {
        if (this.visibleColumnsValue.includes(column.data)) {
          cols.push(column);
        }
      });

      visibleColumns = cols;
    }

    this.hot = new Handsontable(this.element, {
      data: this.rows,
      rowHeaders: true,
      colHeaders: true,
      height: "100%",
      autoWrapRow: true,
      autoWrapCol: true,
      columns: visibleColumns,
      licenseKey: "non-commercial-and-evaluation", // for non-commercial use only
      minSpareRows: 0,
      fillHandle: false,
      width: "100%",
      selectionMode: "single",
      manualColumnMove: true,
      fixedColumnsStart: 4,
      manualColumnFreeze: true,
      hiddenColumns: {
        indicators: true,
      },
      rowHeights: 30,
      manualColumnResize: false,
      viewportColumnRenderingOffset: 26,
      currentRowClassName: "current-row",
      contextMenu: {
        items: {
          new_deployment: {
            name: "New Deployment",
            async callback() {
              await controller.addRow();
            },
          },
          separator1: ContextMenu.SEPARATOR,
          hide_columns: {
            name: "Hide Columns",
            callback() {
              controller.hideColumns();
            },
          },
          show_hidden_columns: {
            name: "Show Hidden Columns",
            callback() {
              controller.showHiddenColumns();
            },
          },
          separator2: ContextMenu.SEPARATOR,
          freeze_column: {},
          unfreeze_column: {},
          separator3: ContextMenu.SEPARATOR,
          remove_row: {},
          separator4: ContextMenu.SEPARATOR,
          reset_table_state: {
            name: "Reset Table State",
            async callback() {
              await controller.resetTableState();
            },
          },
        },
      },
      afterChange: (changes, source) => this.afterChange(changes, source),
      beforeRemoveRow: (index, amount, physicalRows, source) =>
        this.removeRow(index, amount, physicalRows, source),
      afterSelection: (row, column) => this.afterSelection(row, column),
      afterDeselect: () => this.afterDeselect(),
      afterColumnMove: async (movedColumns) =>
        await this.afterColumnMove(movedColumns),
      afterColumnResize: async () => await this.afterColumnResize(),
      renderer: (instance, td, row, col, prop, value, cellProperties) => {
        if (!value) {
          td.innerHTML = `<div class="truncated"></div>`;
          return td;
        }

        td.innerHTML = `<div class="truncated">${value}</div>`;
      },
    });

    this.channel = consumer.subscriptions.create("DeploymentsTableChannel", {
      connected() {
        // Called when the subscription is ready for use on the server
      },

      disconnected() {
        // Called when the subscription has been terminated by the server
      },

      received(data) {
        // Called when there's incoming data on the websocket for this channel
        switch (data.type) {
          case "CELL_UPDATE":
            controller.cellUpdateCallback(data.row_id, data.row);
            break;
          case "ROW_ADDED":
            controller.rowAddedCallback(data.row);
            break;
          case "ROW_REMOVED":
            controller.rowRemovedCallback(data.row_id);
            break;
          case "CELL_SELECTED":
            if (data.user.toString() === controller.currentUserId) return;

            controller.cellSelectedCallback(
              data.row,
              data.column,
              data.current_user,
              data.user,
            );

            break;
          case "CELL_DESELECTED":
            if (data.user.toString() === controller.currentUserId) return;

            controller.cellDeselectedCallback(data.user);
            break;
        }
      },
    });

    await this.loadTableSettings();
  }

  afterChange(changes, source) {
    if (source === "loadData" || !changes) {
      return;
    }

    // These columns need to be converted to YYYY-MM-DD
    const dateColumns = ["lease_funding_date", "agent_split_status"];

    // These columns are association records and need to be sent as
    // as an IDs
    const associationColumns = [
      "merchant",
      "sales_channel",
      "processor",
      "platform",
      "deployment_status",
    ];

    const monetizeColumns = [
      "per_transaction",
      "projected_volume",
      "actual_volume",
      "hardware_cost",
      "overhead",
      "monthly_payment",
      "ae_mid_commission",
      "sm_commission",
      "gsm_commission",
      "cash_bonus",
    ];

    changes.forEach(([rowIndex, columnId, oldValue, newValue]) => {
      // Skip if it's the last empty row (minSpareRows)
      if (rowIndex >= this.rows.length) return;

      // Get the deployment ID from the rows data
      const deploymentId = this.rows[rowIndex].id;

      // TODO: We need to find a better way to do this
      // These columns need to be sent in as an int of cents
      if (monetizeColumns.includes(columnId)) {
        newValue = newValue / 100;
      }

      if (dateColumns.includes(columnId)) {
        if (newValue) {
          const date = new Date(newValue);
          newValue = date.toISOString().split("T")[0];
        } else {
          newValue = null;
        }
      }

      if (associationColumns.includes(columnId)) {
        columnId = `${columnId}_id`;
        newValue = newValue?.id ?? "";
      }

      // Only update if value actually changed
      if (oldValue !== newValue) {
        updateDeployment(deploymentId, columnId, newValue);
      }
    });
  }

  async addRow() {
    await createDeployment();
  }

  removeRow(index, amount, physicalRows, source) {
    if (!confirm("Are you sure you want to delete this row?")) return false;

    if (source === "ContextMenu.removeRow") {
      physicalRows.forEach((row) => {
        deleteDeployment(this.rows[row].id);
      });
    }
  }

  matchesExcludedStatusFilter(row) {
    if (this.excludedStatusesValue.length === 0) return true;

    return !this.excludedStatusesValue.includes(row.deployment_status?.name);
  }

  cellUpdateCallback(rowId, updatedRow) {
    const rowIndex = this.rows.findIndex((row) => row.id === rowId);
    const stillMatches = this.matchesExcludedStatusFilter(updatedRow);

    if (rowIndex !== -1 && stillMatches) {
      this.rows[rowIndex] = updatedRow;
    } else if (rowIndex !== -1 && !stillMatches) {
      this.rows = this.rows.filter((row) => row.id !== rowId);
    } else if (rowIndex === -1 && stillMatches) {
      this.rows.push(updatedRow);
    }

    this.hot.updateSettings({
      data: this.rows,
    });
  }

  rowAddedCallback(row) {
    if (!this.matchesExcludedStatusFilter(row)) return;

    this.rows.push(row);

    this.hot.updateSettings({
      data: this.rows,
    });
  }

  rowRemovedCallback(rowId) {
    this.rows = this.rows.filter((row) => row.id !== rowId);

    this.hot.updateSettings({
      data: this.rows,
    });
  }

  afterSelection(row, column) {
    // Store current selection
    this.lastSelectedCell = { row, column };

    // Notify about new selection
    this.channel.perform("cell_selected", { row: row, column: column });
  }

  cellSelectedCallback(row, column, user, userId) {
    document
      .querySelectorAll(".user-selection[data-user='" + userId + "']")
      .forEach((element) => {
        element.remove();
      });

    // Add visual indicator to the selected cell
    const cellElement = this.hot.getCell(row, column);
    if (cellElement) {
      const selectElement = document.createElement("div");
      selectElement.dataset.user = userId;

      // Get color from user's live circle
      const liveCircle = document.querySelector(`#user_${userId}`).dataset
        .color;
      selectElement.style.setProperty("--color", liveCircle);

      selectElement.classList.add("user-selection");

      cellElement.appendChild(selectElement);
    }
  }

  afterDeselect() {
    this.channel.perform("cell_deselected", { user: this.currentUserId });
  }

  cellDeselectedCallback(user) {
    document
      .querySelectorAll(".user-selection[data-user='" + user + "']")
      .forEach((element) => {
        element.remove();
      });
  }

  async showHiddenColumns() {
    const hiddenColumnsPlugin = this.hot.getPlugin("hiddenColumns");
    hiddenColumnsPlugin.showColumns(hiddenColumnsPlugin.getHiddenColumns());

    // Save to persistent state
    await saveTableState(
      "hiddenColumns",
      hiddenColumnsPlugin.getHiddenColumns(),
    );

    this.hot.render();
  }

  async hideColumns() {
    const hiddenColumnsPlugin = this.hot.getPlugin("hiddenColumns");
    const selection = this.hot.getSelectedLast();

    if (Array.isArray(selection)) {
      const [, fromColumn, , toColumn] = selection;
      hiddenColumnsPlugin.hideColumn(fromColumn);
    } else {
      hiddenColumnsPlugin.hideColumn(selection);
    }

    // Save to persistent state
    await saveTableState(
      "hiddenColumns",
      hiddenColumnsPlugin.getHiddenColumns(),
    );

    this.hot.render();
  }

  async resetTableState() {
    await resetTableState();

    // this.hot.updateSettings({
    //   hiddenColumns: {
    //     indicators: true,
    //     columns: [],
    //   },
    //   manualColumnMove: [],
    // });

    // this.hot.render();

    // TODO: This is a hack to force the table to reload
    window.location.reload();
  }

  async loadTableSettings() {
    this.hot.updateSettings({
      hiddenColumns: {
        indicators: true,
        columns: this.tableState?.hiddenColumns ?? [],
      },
      manualColumnMove: this.tableState?.columnOrder ?? [],
      manualColumnResize: this.tableState?.columnWidths ?? [],
    });

    this.hot.render();
  }

  async afterColumnMove(movedColumns) {
    const columnOrder = this.hot.columnIndexMapper.getIndexesSequence();

    if (movedColumns.length > 0) {
      await saveTableState("columnOrder", columnOrder);
    }
  }

  async afterColumnResize() {
    const columnWidths = [];
    const columnCount = this.hot.countCols();

    for (let i = 0; i < columnCount; i++) {
      columnWidths.push(this.hot.getColWidth(i));
    }

    await saveTableState("columnWidths", columnWidths);
  }
}
